import type {
  AdminBannerView,
  AdminBlogCategoryView,
  AdminBlogPostListItem,
  AdminBlogPostView,
  AdminJourneyListItem,
  AdminJourneyView,
  AdminFaqView,
  AdminHomepageSectionView,
  AdminMenuView,
  AdminNotificationList,
  AdminPageListItem,
  AdminPageView,
  AdminReviewListItem,
  AdminRoleOption,
  AdminTestimonialView,
  AdminUserCreateInput,
  AdminUserListItem,
  AdminUserUpdateInput,
  AdminUserView,
  AuditLogEntry,
  BannerInput,
  BlogCategoryInput,
  BlogPostInput,
  JourneyInput,
  BookingRescheduleInput,
  CalendarBoardView,
  CmsReference,
  CorporateAccountInput,
  CorporateAccountView,
  CrmReference,
  CustomerListItem,
  CustomerProfileUpdate,
  CustomerView,
  DashboardView,
  ExportFormat,
  FaqInput,
  FeatureFlagSummary,
  HomepageSectionUpdate,
  Locale,
  MailPreview,
  MailTemplateKey,
  MailTemplateListItem,
  MailTemplateUpdate,
  MailTemplateView,
  MediaLibraryItem,
  MediaUpdateInput,
  MenuSaveInput,
  PageInput,
  Paginated,
  PermissionGroup,
  ReportKey,
  ReportView,
  ReviewModerationInput,
  RoleCreateInput,
  RoleSummary,
  SettingKey,
  SettingsOverview,
  SettingView,
  SmsTemplateKey,
  SmsTemplateUpdate,
  SmsTemplateView,
  TestimonialInput,
} from '@duvamarine/shared';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { api } from './api';
import { downloadFile, query } from './finance-api';

/* Phase 5 admin screens: dashboard, calendar, CRM, reports, CMS, reviews, templates, notifications,
 * users & roles, audit log and settings. */

type Params = Record<string, string | number | boolean | null | undefined>;

const ADMIN = ['admin'] as const;
const key = (...parts: (string | number | null | undefined)[]) => [...ADMIN, ...parts] as const;

function useLanguage(): string {
  const { i18n } = useTranslation();
  return i18n.resolvedLanguage ?? 'tr';
}

function useInvalidate() {
  const queryClient = useQueryClient();
  return (...prefix: string[]) =>
    void queryClient.invalidateQueries({ queryKey: [...ADMIN, ...prefix] });
}

// ---------- Dashboard & calendar ----------

export function useDashboard() {
  const language = useLanguage();
  return useQuery({
    queryKey: key('dashboard', language),
    queryFn: () => api.get<DashboardView>('/admin/dashboard'),
    refetchInterval: 5 * 60_000,
  });
}

export function useCalendarBoard(params: {
  from: string;
  to: string;
  categoryId: string;
  q: string;
}) {
  return useQuery({
    queryKey: key('calendar', params.from, params.to, params.categoryId, params.q),
    queryFn: () => api.get<CalendarBoardView>(`/admin/calendar${query(params)}`),
    placeholderData: keepPreviousData,
  });
}

export function useReschedule() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: ({ bookingId, input }: { bookingId: string; input: BookingRescheduleInput }) =>
      api.patch<{ startsAt: string; endsAt: string }>(
        `/admin/bookings/${bookingId}/reschedule`,
        input,
      ),
    onSettled: () => {
      invalidate('calendar');
      invalidate('booking');
      invalidate('bookings');
      invalidate('dashboard');
    },
  });
}

// ---------- CRM ----------

export function useCustomers(params: Params) {
  return useQuery({
    queryKey: key('customers', JSON.stringify(params)),
    queryFn: () => api.get<Paginated<CustomerListItem>>(`/admin/customers${query(params)}`),
    placeholderData: keepPreviousData,
  });
}

export function useCrmReference() {
  return useQuery({
    queryKey: key('customers', 'reference'),
    queryFn: () => api.get<CrmReference>('/admin/customers/reference'),
    staleTime: 60_000,
  });
}

export function useCustomer(id: string) {
  return useQuery({
    queryKey: key('customer', id),
    queryFn: () => api.get<CustomerView>(`/admin/customers/${id}`),
  });
}

export function useUpdateCustomer(id: string) {
  const queryClient = useQueryClient();
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (input: CustomerProfileUpdate) =>
      api.put<CustomerView>(`/admin/customers/${id}`, input),
    onSuccess: (view) => {
      queryClient.setQueryData(key('customer', id), view);
      invalidate('customers');
    },
  });
}

export function useCorporateAccounts(params: Params) {
  return useQuery({
    queryKey: key('corporate', JSON.stringify(params)),
    queryFn: () =>
      api.get<Paginated<CorporateAccountView>>(`/admin/corporate-accounts${query(params)}`),
    placeholderData: keepPreviousData,
  });
}

export function useSaveCorporateAccount() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: ({ id, input }: { id: string | null; input: CorporateAccountInput }) =>
      id
        ? api.put<CorporateAccountView>(`/admin/corporate-accounts/${id}`, input)
        : api.post<CorporateAccountView>('/admin/corporate-accounts', input),
    onSuccess: () => {
      invalidate('corporate');
      invalidate('customers');
    },
  });
}

// ---------- Reports ----------

export function useReport(report: ReportKey, params: Params) {
  const language = useLanguage();
  return useQuery({
    queryKey: key('reports', report, JSON.stringify(params), language),
    queryFn: () => api.get<ReportView>(`/admin/reports/${report}${query(params)}`),
    placeholderData: keepPreviousData,
  });
}

export function exportReport(report: ReportKey, format: ExportFormat, params: Params) {
  const stamp = new Date().toISOString().slice(0, 10);
  return downloadFile(
    `/admin/reports/${report}/export${query({ ...params, format })}`,
    `duvamarine-report-${report}-${stamp}.${format}`,
  );
}

export function useSendScheduledReport() {
  return useMutation({
    mutationFn: () => api.post<{ recipients: number }>('/admin/reports/schedule/send', {}),
  });
}

// ---------- CMS ----------

export function useCmsReference() {
  return useQuery({
    queryKey: key('cms', 'reference'),
    queryFn: () => api.get<CmsReference>('/admin/cms/reference'),
    staleTime: 60_000,
  });
}

export function useCmsPages(params: Params) {
  return useQuery({
    queryKey: key('cms', 'pages', JSON.stringify(params)),
    queryFn: () => api.get<Paginated<AdminPageListItem>>(`/admin/cms/pages${query(params)}`),
    placeholderData: keepPreviousData,
  });
}

export function useCmsPage(id: string | null) {
  return useQuery({
    queryKey: key('cms', 'page', id),
    queryFn: () => api.get<AdminPageView>(`/admin/cms/pages/${id ?? ''}`),
    enabled: id !== null,
  });
}

export function useSavePage() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: ({ id, input }: { id: string | null; input: PageInput }) =>
      id
        ? api.put<AdminPageView>(`/admin/cms/pages/${id}`, input)
        : api.post<AdminPageView>('/admin/cms/pages', input),
    onSuccess: () => invalidate('cms'),
  });
}

export function useRemovePage() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (id: string) => api.delete<void>(`/admin/cms/pages/${id}`),
    onSuccess: () => invalidate('cms'),
  });
}

export function useBlogPosts(params: Params) {
  return useQuery({
    queryKey: key('cms', 'posts', JSON.stringify(params)),
    queryFn: () =>
      api.get<Paginated<AdminBlogPostListItem>>(`/admin/cms/blog/posts${query(params)}`),
    placeholderData: keepPreviousData,
  });
}

export function useBlogPost(id: string | null) {
  return useQuery({
    queryKey: key('cms', 'post', id),
    queryFn: () => api.get<AdminBlogPostView>(`/admin/cms/blog/posts/${id ?? ''}`),
    enabled: id !== null,
  });
}

export function useSaveBlogPost() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: ({ id, input }: { id: string | null; input: BlogPostInput }) =>
      id
        ? api.put<AdminBlogPostView>(`/admin/cms/blog/posts/${id}`, input)
        : api.post<AdminBlogPostView>('/admin/cms/blog/posts', input),
    onSuccess: () => invalidate('cms'),
  });
}

export function useRemoveBlogPost() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (id: string) => api.delete<void>(`/admin/cms/blog/posts/${id}`),
    onSuccess: () => invalidate('cms'),
  });
}

// ---------- Signature Journeys (Wave 7) ----------

export function useJourneysAdmin(params: Params) {
  return useQuery({
    queryKey: key('cms', 'journeys', JSON.stringify(params)),
    queryFn: () => api.get<Paginated<AdminJourneyListItem>>(`/admin/journeys${query(params)}`),
    placeholderData: keepPreviousData,
  });
}

export function useJourneyAdmin(id: string | null) {
  return useQuery({
    queryKey: key('cms', 'journey', id),
    queryFn: () => api.get<AdminJourneyView>(`/admin/journeys/${id ?? ''}`),
    enabled: id !== null,
  });
}

export function useSaveJourney() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: ({ id, input }: { id: string | null; input: JourneyInput }) =>
      id
        ? api.put<AdminJourneyView>(`/admin/journeys/${id}`, input)
        : api.post<AdminJourneyView>('/admin/journeys', input),
    onSuccess: () => invalidate('cms'),
  });
}

export function useRemoveJourney() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (id: string) => api.delete<void>(`/admin/journeys/${id}`),
    onSuccess: () => invalidate('cms'),
  });
}

/** Small CMS collections: list, save and delete all return the whole list. */
function cmsCollection<View, Input>(path: string, name: string) {
  return {
    useList: () => useQuery({ queryKey: key('cms', name), queryFn: () => api.get<View[]>(path) }),
    useSave: () => {
      const queryClient = useQueryClient();
      return useMutation({
        mutationFn: ({ id, input }: { id: string | null; input: Input }) =>
          id ? api.put<View[]>(`${path}/${id}`, input) : api.post<View[]>(path, input),
        onSuccess: (list) => queryClient.setQueryData(key('cms', name), list),
      });
    },
    useRemove: () => {
      const queryClient = useQueryClient();
      return useMutation({
        mutationFn: (id: string) => api.delete<View[]>(`${path}/${id}`),
        onSuccess: (list) => queryClient.setQueryData(key('cms', name), list),
      });
    },
  };
}

export const blogCategories = cmsCollection<AdminBlogCategoryView, BlogCategoryInput>(
  '/admin/cms/blog/categories',
  'blog-categories',
);
export const faqs = cmsCollection<AdminFaqView, FaqInput>('/admin/cms/faqs', 'faqs');
export const testimonials = cmsCollection<AdminTestimonialView, TestimonialInput>(
  '/admin/cms/testimonials',
  'testimonials',
);
export const banners = cmsCollection<AdminBannerView, BannerInput>('/admin/cms/banners', 'banners');

export function useMenus() {
  return useQuery({
    queryKey: key('cms', 'menus'),
    queryFn: () => api.get<AdminMenuView[]>('/admin/cms/menus'),
  });
}

export function useSaveMenu() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ menuKey, input }: { menuKey: string; input: MenuSaveInput }) =>
      api.put<AdminMenuView[]>(`/admin/cms/menus/${menuKey}`, input),
    onSuccess: (menus) => queryClient.setQueryData(key('cms', 'menus'), menus),
  });
}

export function useHomeSections() {
  return useQuery({
    queryKey: key('cms', 'home'),
    queryFn: () => api.get<AdminHomepageSectionView[]>('/admin/cms/home'),
  });
}

export function useHomeMutations() {
  const queryClient = useQueryClient();
  const set = (sections: AdminHomepageSectionView[]) =>
    queryClient.setQueryData(key('cms', 'home'), sections);
  return {
    update: useMutation({
      mutationFn: ({ id, input }: { id: string; input: HomepageSectionUpdate }) =>
        api.put<AdminHomepageSectionView[]>(`/admin/cms/home/${id}`, input),
      onSuccess: set,
    }),
    reorder: useMutation({
      mutationFn: (ids: string[]) =>
        api.post<AdminHomepageSectionView[]>('/admin/cms/home/reorder', { ids }),
      onSuccess: set,
    }),
  };
}

export function useMediaLibrary(params: Params, enabled = true) {
  return useQuery({
    queryKey: key('media', JSON.stringify(params)),
    queryFn: () => api.get<Paginated<MediaLibraryItem>>(`/admin/media${query(params)}`),
    placeholderData: keepPreviousData,
    enabled,
  });
}

export function useMediaMutations() {
  const invalidate = useInvalidate();
  return {
    upload: useMutation({
      mutationFn: ({ files, folder }: { files: File[]; folder: string }) => {
        const body = new FormData();
        for (const file of files) body.append('files', file);
        return api.post<MediaLibraryItem[]>(`/admin/media${query({ folder })}`, body);
      },
      onSuccess: () => invalidate('media'),
    }),
    update: useMutation({
      mutationFn: ({ id, input }: { id: string; input: MediaUpdateInput }) =>
        api.patch<MediaLibraryItem>(`/admin/media/${id}`, input),
      onSuccess: () => invalidate('media'),
    }),
    remove: useMutation({
      mutationFn: (id: string) => api.delete<void>(`/admin/media/${id}`),
      onSuccess: () => invalidate('media'),
    }),
  };
}

// ---------- Reviews ----------

export function useReviews(params: Params) {
  return useQuery({
    queryKey: key('reviews', JSON.stringify(params)),
    queryFn: () => api.get<Paginated<AdminReviewListItem>>(`/admin/reviews${query(params)}`),
    placeholderData: keepPreviousData,
  });
}

export function useModerateReview() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: ReviewModerationInput }) =>
      api.patch<AdminReviewListItem>(`/admin/reviews/${id}`, input),
    onSuccess: () => {
      invalidate('reviews');
      invalidate('cms', 'testimonials');
      invalidate('dashboard');
    },
  });
}

// ---------- Templates & notifications ----------

export function useMailTemplates() {
  return useQuery({
    queryKey: key('templates', 'mail'),
    queryFn: () => api.get<MailTemplateListItem[]>('/admin/templates/mail'),
  });
}

export function useMailTemplate(templateKey: MailTemplateKey | null, locale: Locale) {
  return useQuery({
    queryKey: key('templates', 'mail', templateKey, locale),
    queryFn: () =>
      api.get<MailTemplateView>(`/admin/templates/mail/${templateKey ?? ''}/${locale}`),
    enabled: templateKey !== null,
  });
}

export function useMailTemplateMutations(templateKey: MailTemplateKey, locale: Locale) {
  const queryClient = useQueryClient();
  const invalidate = useInvalidate();
  const set = (view: MailTemplateView) => {
    queryClient.setQueryData(key('templates', 'mail', templateKey, locale), view);
    invalidate('templates', 'mail');
  };
  const base = `/admin/templates/mail/${templateKey}/${locale}`;
  return {
    save: useMutation({
      mutationFn: (input: MailTemplateUpdate) => api.put<MailTemplateView>(base, input),
      onSuccess: set,
    }),
    reset: useMutation({
      mutationFn: () => api.delete<MailTemplateView>(base),
      onSuccess: set,
    }),
    preview: useMutation({
      mutationFn: (input: { subject: string; body: string }) =>
        api.post<MailPreview>(`${base}/preview`, input),
    }),
  };
}

export function useSmsTemplates(locale: Locale) {
  return useQuery({
    queryKey: key('templates', 'sms', locale),
    queryFn: () => api.get<SmsTemplateView[]>(`/admin/templates/sms/${locale}`),
  });
}

export function useSmsTemplateMutations(locale: Locale) {
  const invalidate = useInvalidate();
  return {
    save: useMutation({
      mutationFn: ({
        templateKey,
        input,
      }: {
        templateKey: SmsTemplateKey;
        input: SmsTemplateUpdate;
      }) => api.put<SmsTemplateView>(`/admin/templates/sms/${templateKey}/${locale}`, input),
      onSuccess: () => invalidate('templates', 'sms'),
    }),
    reset: useMutation({
      mutationFn: (templateKey: SmsTemplateKey) =>
        api.delete<SmsTemplateView>(`/admin/templates/sms/${templateKey}/${locale}`),
      onSuccess: () => invalidate('templates', 'sms'),
    }),
  };
}

export function useNotifications(params: Params) {
  return useQuery({
    queryKey: key('notifications', JSON.stringify(params)),
    queryFn: () => api.get<AdminNotificationList>(`/admin/notifications${query(params)}`),
  });
}

export function useMarkNotifications() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (id: string | null) =>
      api.post<{ unread: number }>(
        id ? `/admin/notifications/${id}/read` : '/admin/notifications/read-all',
        {},
      ),
    onSuccess: () => invalidate('notifications'),
  });
}

// ---------- Users, roles, audit ----------

export function useAdminUsers(params: Params) {
  return useQuery({
    queryKey: key('users', JSON.stringify(params)),
    queryFn: () => api.get<Paginated<AdminUserListItem>>(`/admin/users${query(params)}`),
    placeholderData: keepPreviousData,
  });
}

export function useRoleOptions() {
  return useQuery({
    queryKey: key('users', 'roles'),
    queryFn: () => api.get<AdminRoleOption[]>('/admin/users/roles'),
    staleTime: 60_000,
  });
}

export function useAdminUser(id: string | null) {
  return useQuery({
    queryKey: key('user', id),
    queryFn: () => api.get<AdminUserView>(`/admin/users/${id ?? ''}`),
    enabled: id !== null,
  });
}

export function useAdminUserMutations() {
  const queryClient = useQueryClient();
  const invalidate = useInvalidate();
  const set = (view: AdminUserView) => {
    queryClient.setQueryData(key('user', view.id), view);
    invalidate('users');
  };
  return {
    create: useMutation({
      mutationFn: (input: AdminUserCreateInput) => api.post<AdminUserView>('/admin/users', input),
      onSuccess: set,
    }),
    update: useMutation({
      mutationFn: ({ id, input }: { id: string; input: AdminUserUpdateInput }) =>
        api.put<AdminUserView>(`/admin/users/${id}`, input),
      onSuccess: set,
    }),
    passwordLink: useMutation({
      mutationFn: (id: string) => api.post<void>(`/admin/users/${id}/password-link`, {}),
    }),
    resetTwoFactor: useMutation({
      mutationFn: (id: string) => api.post<AdminUserView>(`/admin/users/${id}/reset-2fa`, {}),
      onSuccess: set,
    }),
    revokeSession: useMutation({
      mutationFn: ({ id, familyId }: { id: string; familyId: string }) =>
        api.delete<void>(`/admin/users/${id}/sessions/${familyId}`),
      onSuccess: (_result, variables) => invalidate('user', variables.id),
    }),
  };
}

export function useRoles() {
  return useQuery({
    queryKey: key('roles'),
    queryFn: () => api.get<RoleSummary[]>('/admin/rbac/roles'),
  });
}

export function usePermissionGroups() {
  return useQuery({
    queryKey: key('permissions'),
    queryFn: () => api.get<PermissionGroup[]>('/admin/rbac/permissions'),
    staleTime: 10 * 60_000,
  });
}

export function useRoleMutations() {
  const invalidate = useInvalidate();
  return {
    setPermissions: useMutation({
      mutationFn: ({ id, permissions }: { id: string; permissions: string[] }) =>
        api.put<RoleSummary>(`/admin/rbac/roles/${id}/permissions`, { permissions }),
      onSuccess: () => invalidate('roles'),
    }),
    create: useMutation({
      mutationFn: (input: RoleCreateInput) => api.post<RoleSummary>('/admin/rbac/roles', input),
      onSuccess: () => invalidate('roles'),
    }),
    remove: useMutation({
      mutationFn: (id: string) => api.delete<void>(`/admin/rbac/roles/${id}`),
      onSuccess: () => invalidate('roles'),
    }),
  };
}

export function useAuditLog(params: Params) {
  return useQuery({
    queryKey: key('audit', JSON.stringify(params)),
    queryFn: () => api.get<Paginated<AuditLogEntry>>(`/admin/audit-logs${query(params)}`),
    placeholderData: keepPreviousData,
  });
}

// ---------- Settings & feature flags ----------

export function useSettings(enabled = true) {
  return useQuery({
    queryKey: key('settings'),
    queryFn: () => api.get<SettingsOverview>('/admin/settings'),
    enabled,
  });
}

export function useSaveSetting() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: ({ settingKey, value }: { settingKey: SettingKey; value: unknown }) =>
      api.put<SettingView>(`/admin/settings/${settingKey}`, { value }),
    onSuccess: () => invalidate('settings'),
  });
}

export function useFeatureFlags() {
  return useQuery({
    queryKey: key('feature-flags'),
    queryFn: () => api.get<FeatureFlagSummary[]>('/admin/feature-flags'),
  });
}

export function useToggleFeatureFlag() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: ({ flag, isEnabled }: { flag: string; isEnabled: boolean }) =>
      api.patch<FeatureFlagSummary>(`/admin/feature-flags/${flag}`, { isEnabled }),
    onSuccess: () => invalidate('feature-flags'),
  });
}
